import Image from '@/components/Image';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import Link from 'next/link';
import { Children, cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';

type ReturnIssueItem = {
  title: string;
  content: () => ReactNode;
};

const CONTACT_LINK_TEXT = 'Kontaktiere uns';
const CONTACT_LINK_HREF = 'https://de.zeelool.com/contact-zeelool';

const RETURN_REFUND_INTRO_LINES: ReactNode[] = [
  <>
    Zeelool hat sich zum Ziel gesetzt, Ihnen eine perfekte Brille zum besten Preis anzubieten. Wenn
    Sie Probleme haben, zögern Sie bitte nicht, uns zu kontaktieren.
  </>,
  <>
    Alle auf unserer Website verkauften Brillen werden individuell nach Deiner Auswahl angefertigt,
    und die Produktion beginnt unmittelbar nach der Bestellbestätigung. Maßgefertigte Waren sind in
    der Regel von der gesetzlichen Widerrufsfrist für Fernabsatzverträge ausgenommen. Dennoch bieten
    wir Dir freiwillig eine großzügigere Rückgabefrist von 45 Tagen ab dem Datum des Wareneingangs
    an. Wenn du deine maßgefertigten Brillen zurückgeben möchtest, Kontaktiere uns bitte, um die
    Anfrage zu stellen. Wir melden uns innerhalb von 24 Stunden bei dir und führen dich durch den
    Rückgabeprozess.
  </>,
];

const replaceContactLinkText = (text: string): ReactNode => {
  if (!text.includes(CONTACT_LINK_TEXT)) {
    return text;
  }

  const parts = text.split(CONTACT_LINK_TEXT);

  return parts.flatMap((part, index) => {
    if (index === parts.length - 1) {
      return part;
    }

    return [
      part,
      <Link
        key={`${CONTACT_LINK_TEXT}-${index}`}
        href={CONTACT_LINK_HREF}
        className="text-[#0000ee]"
      >
        {CONTACT_LINK_TEXT}
      </Link>,
    ];
  });
};

const renderContentWithContactLink = (content: ReactNode): ReactNode => {
  if (typeof content === 'string') {
    return replaceContactLinkText(content);
  }

  if (Array.isArray(content)) {
    const nodes = content as ReactNode[];

    return nodes.map((item) => renderContentWithContactLink(item));
  }

  if (!isValidElement(content)) {
    return content;
  }

  const element = content as ReactElement<{ children?: ReactNode }>;

  return cloneElement(
    element,
    undefined,
    Children.map(element.props.children, (child) => renderContentWithContactLink(child)),
  );
};

const RETURN_ISSUE_ITEMS: ReturnIssueItem[] = [
  {
    title: '· Adresse der Rücksendung:',
    content: () => (
      <>
        Wenn Sie den Artikel zurückschicken möchten, bitte senden Sie Ihre Bestellnummer und den
        Grund für Rücksendung über unser Kontaktiere uns. Unser Kundendienst wird Ihnen weiter die
        Rücksendeadresse mitteilen. Achtung: Die auf dem Paket angegebene Adresse ist nicht korrekte
        Rücksendeadresse. Rückerstattungen können nicht bearbeitet werden, wenn Sie an falsche
        Adresse schicken. Wir bitten um Verständnis.
      </>
    ),
  },
  {
    title: '· Farbunterschied:',
    content: () => (
      <>
        Aufgrund unterschiedlicher Lichtverhältnisse in Sonnenlicht und Bildschirm können einige
        visuelle Unterschiede auftreten. Waren sind in Naturalien vorzuziehen. Bitte senden Sie Ihre
        Bestellnummer, ein Foto der erhaltenen Brille und eine Problembeschreibung über unser
        Kontaktiere uns. Wir werden uns innerhalb von 24 Stunden um Ihr Problem kümmern. Wenn Sie
        der Meinung sind, dass die Brille nicht Ihren Erwartungen entspricht, wenden Sie sich bitte
        innerhalb von 45 Tagen nach Erhalt der Brille an unseren Kundendienst.
      </>
    ),
  },
  {
    title: '· Über Rezept:',
    content: () => (
      <>
        Wenn die Brille nicht klar ist, überprüfen Sie bitte zuerst Ihr Rezept. Wenn das Rezept
        korrekt ist, können Sie es innerhalb der 45-tägigen Garantiezeit zur Überprüfung an uns
        zurücksenden, und wir werden Ihnen einen Ersatz oder eine Rückerstattung gewähren, wenn wir
        dafür verantwortlich sind; wenn Sie leider das falsche Rezept ausfüllen, können Sie einen
        speziellen Gutschein für Ihre neue Brille erhalten oder eine teilweise Rückerstattung
        beantragen. Bitte senden Sie Ihre Bestellnummer, Ihr Rezept oder Brillenpass und eine
        Problembeschreibung über unser Kontaktiere uns. Wir werden uns innerhalb von 24 Stunden um
        Ihr Problem kümmern.
      </>
    ),
  },
  {
    title: '· Zusätzliches Rezept:',
    content: () => (
      <>
        Innerhalb von 12 Stunden nach der Bestellung, können Sie sich an den Kundendienst wenden, um
        das Brillenrezept ändern. Danach können Sie es nicht mehr ändern. Bitte senden Sie Ihre
        Bestellnummer, ein Bild Ihres Rezeptes und eine Problembeschreibung über unser Kontaktiere
        uns. Wir werden Ihnen innerhalb von 24 Stunden bei der Änderung helfen.
      </>
    ),
  },
  {
    title: '· Über die Größe:',
    content: () => (
      <>
        Aufgrund unterschiedlicher Messtechniken und Winkel tritt eine Abweichung der
        Rahmenparameter von ca. 1-2 mm auf, wobei die tatsächlichen Parameter in natura
        vorherrschen. Sie können die passende Brille finden, indem Sie sich die Größentabelle auf
        jeder Brillendetailseite ansehen. Wenn die Brille jedoch nicht geeignet ist, weil Sie
        versehentlich die falschen Größenangaben gelesen haben, sollten Sie uns innerhalb von 45
        Tagen nach Erhalt der Brille kontaktieren, um das Problem zu lösen. Bitte senden Sie Ihre
        Bestellnummer, Produktname und eine Problembeschreibung über unser Kontaktiere uns und wir
        werden uns innerhalb von 24 Stunden um Ihr Problem kümmern.
      </>
    ),
  },
  {
    title: '· Falsche Brille erhalten/fehlende Brille:',
    content: () => (
      <>
        Wenn Sie eine falsche Brille erhalten, die nicht der von Ihnen bestellten entspricht, senden
        Sie bitte Ihre Bestellnummer, ein Foto der erhaltenen Brille, ein Foto des Pakets und eine
        Problembeschreibung über unser Kontaktiere uns. Nach der Überprüfung senden wir Ihnen
        kostenlos eine neue Brille zu oder erstatten Ihnen den vollen Betrag.
      </>
    ),
  },
  {
    title: '· Nicht verfügbarer Gutschein:',
    content: () => (
      <>
        Aufgrund der vielen Arten von Gutscheinen auf der Website sind die Bedingungen für die
        Anwendung unterschiedlich. Sie haben vergessen, den Gutschein vor oder nach Ihrer Bestellung
        einzulösen, oder der Gutschein ist nicht verfügbar. Geben Sie bitte den Namen des Gutscheins
        an, den Sie nicht einlösen können (z.B. 5 € Rabatt über 50, zweite Rahmen 50% Rabatt), und
        senden Sie ihn über unser Kontaktiere uns.
      </>
    ),
  },
  {
    title: '· Zollgebühren:',
    content: () => (
      <>
        Wenn Sie vom Zoll darauf hingewiesen werden, dass Sie Zollgebühren zahlen müssen, tun Sie
        dies bitte. Bitte senden Sie eine Kopie Ihrer Rechnung über unser Kontaktiere uns und wir
        werden Ihnen den Betrag erstatten. (Hinweis: Wir übernehmen die Kosten für Zollgebühren bis
        zu 150 €, alle darüber hinausgehenden Kosten tragen Sie bitte selbst.)
      </>
    ),
  },
  {
    title: '· Lieferadresse:',
    content: () => (
      <>
        Bitte achten Sie bei der Eingabe Ihrer Adresse auf folgende Angaben: ① vollständiger Name ②
        Straße ( besonders die Hausnummer) ③ Postleitzahl ④ Stadt und Bundesland ⑤ Land. Um
        Zustellungsfehler zu vermeiden, überprüfen Sie bitte noch einmal, nachdem Sie Ihre Adresse
        eingegeben haben. Wenn Sie Probleme beim Ausfüllen Ihrer Adresse haben, wenden Sie sich
        bitte an unseren Kundendienst.
      </>
    ),
  },
  {
    title: '· Rezept ausfüllen:',
    content: () => (
      <>
        Wenn Sie nicht wissen, wie Sie Ihr Rezept bei der Bestellung ausfüllen sollen, laden Sie
        bitte Ihr Rezept direkt auf der Rezeptausfüllseite hoch oder scannen Sie es dort ein. Das
        System erkennt und übernimmt die Angaben automatisch. Alternativ können Sie Ihr Rezept auch
        über unserKontaktiere uns an uns senden, und unser Kundenservice wird Sie bei der Eingabe
        unterstützen.
      </>
    ),
  },
  {
    title: '· Die verlorene Brille:',
    content: () => (
      <>
        In folgenden Fällen stellen wir die ursprüngliche Bestellung bedingungslos erneut aus: Der
        Kunde hat einen eindeutigen Verlustnachweis erbracht; Die Post zeigte eine ordnungsgemäße
        Zustellung, und der Kunde kontaktierte die örtliche Post mehrmals ohne Ergebnis. Es gab
        Lieferzeit und Frachtbriefnummer, aber keine logistischen Informationen.
      </>
    ),
  },
  {
    title: '· Transportschäden:',
    content: () => (
      <>
        Wenn die Brille durch den Transport beschädigt wird, ersetzen wir die Brille kostenlos oder
        geben Ihnen eine teilweise Rückerstattung. Bitte senden Sie Ihre Bestellnummer, ein Bild der
        erhaltenen Brille, ein Bild des Pakets und eine Problembeschreibung über unser Kontaktiere
        uns. Wir werden uns innerhalb von 24 Stunden um Ihr Problem kümmern.
      </>
    ),
  },
  {
    title: '· Beschädigt innerhalb von 45 Tagen:',
    content: () => (
      <>
        Kostenlose Rücksendung ist innerhalb von 45 Tagen nach Ankunft der Lieferung möglich, falls
        es sich um einen nicht beabsichtigten Schaden handelt. Bitte senden Sie Ihre Bestellnummer,
        ein Foto Ihrer Brille mit der Stelle, an der der Schaden aufgetreten ist, und eine
        Beschreibung des Problems über unserKontaktiere uns. Wir werden uns innerhalb von 24 Stunden
        um Ihr Problem kümmern. Diese Garantie erstreckt sich nicht auf die normale Abnutzung der
        Rahmen, Gläser oder Beschichtungen Kostenlose Rücksendung ist innerhalb von 45 Tagen nach
        Ankunft der Lieferung möglich, falls es sich um einen nicht beabsichtigten Schaden handelt.
        Diese Garantie erstreckt sich nicht auf die normale Abnutzung der Rahmen, Gläser oder
        Beschichtungen.
      </>
    ),
  },
];

const RETURN_ORDER_CHANGE_SECTION = {
  title: 'Zusätzliches Rezept / Rezeptaktualisierung / Auftragsänderung / Auftragsstornierung',
  description: (
    <>
      Für die Brillen,die ohne Rezept sind, akzeptieren wir zusätzliches Rezept / Auftragsänderung /
      Auftragsstornierung, bevor wir Ihre Bestellung versenden. In Bezug auf die Anforderung der
      Rezeptaktualisierung müssen Sie uns vor der Verarbeitung der Brillengläsern benachrichtigen.
      Bei Preisunterschieden wird ein Aufpreis erhoben. Bitte kontaktieren Sie uns so bald wie
      möglich, damit wir Ihre Bestellung entsprechend und rechtzeitig bearbeiten können.
      <br />
      Aufgrund der schnellen Bearbeitung und Lieferung，Wenn Sie Ihre Bestellung ändern oder
      stornieren möchten, wenden Sie sich bitte innerhalb von 12 Stunden an unser
      Kundendienstteam,um zusätzliche Kosten zu vermeiden. Andernfalls lesen Sie bitte die folgende
      Tabelle, um die Details zu überprüfen. Da alle Brillengläser durch das gegebene Rezept
      angepasst werden, ist für Brillen keine kostenlose Stornierung / Änderung vorgesehen.
      Versandkosten und Differenzen sind nicht enthalten, falls vorhanden. In der oben genannten
      Situationen berechnen die Gebühren wie folgend. (Achtung: Erstattungen für Rückgaben aus
      persönlichen Gründen werden in der zweiten Spalte berechnet.)
    </>
  ),
};

const RETURN_ORDER_CHANGE_TABLE = {
  headers: {
    beforeShipping: 'Vor dem Versand',
    afterShipping: 'Nach dem Versand',
  },
  rows: [
    {
      label: 'Nur Rahmen',
      beforeShipping: 'Kostenlos',
      afterShipping: '50% des Rahmenpreises',
    },
    {
      label: 'Korrekturgläser',
      beforeShipping: '10% des Bestellbetrags',
      afterShipping: '50% des Rahmenpreises + 50% des Gläserpreises',
    },
  ],
};

const RETURN_GUIDE_SECTION = {
  title: 'So geben Sie Ihre Brille zurück',
  items: [
    <>
      <strong> ·</strong> Kontaktieren Sie uns über unser Kontaktiere uns innerhalb von 45 Tagen
      nach Erhalt Ihrer Brille. Alle Rücksendungen müssen in ihrem ungetragenen, ursprünglichen
      Zustand zurückgesandt werden.
    </>,
    <>
      <strong className="font-bb"> ·</strong>Verpacken Sie Ihre Brille sicher und hinterlassen Sie
      eine Notiz mit Ihrem Namen, Ihrer Bestellnummer und einer kurzen Beschreibung des
      Rückgabegrundes; oder eine Kopie des Rezeptes von Ihrem Arzt erhalten haben, damit Zeelool die
      Brille untersuchen kann.
    </>,
    <>
      <strong className="font-bb"> ·</strong>Zeelool würde es bedauern, falls Ihre Brille während
      des Versands verloren gehen würde. Daher wird empfohlen, Ihre Rücksendungen nach einer
      nachvollziehbaren Methode zu versenden. Bitte senden Sie uns die Verfolgungsnummer, wenn Sie
      die Brille an uns senden.
    </>,
    <>
      <strong> ·</strong> Sobald Zeelool die zurückgegebenen Artikel erhalten hat, werden wir gemäß
      Ihrer gewählten Option sofort eine Neusendung veranlassen oder eine Rückerstattung bearbeiten.
    </>,
    <>
      <strong className="font-bb">· Anmerkungen:</strong> Wir übernehmen das Rückporto nicht (außer
      unserer Verantwortung) und Sie müssen es selbst tragen.
    </>,
    <>
      <strong> .</strong> Versandkosten können in folgenden Situationen erstattet werden:
      Paketverlust oder falsche Stile.
    </>,
    <>
      <strong> .</strong>Versandkosten konnten in den folgenden Situationen nicht erstattet werden:
      Abneigung, Größenproblem, falsches Rezept / falsche Adresse.
    </>,
  ] satisfies ReactNode[],
};

/**
 * 德语静态内容组件
 * 文本还是需要抽成配置，这样子 pc / wap 只需要核对一次
 * 需求：https://devops.aliyun.com/projex/project/ce85697916dd382a20b8106373/req/5f79fe775bfe756df060632bc2
 * 终版文本：https://alidocs.dingtalk.com/i/nodes/6LeBq413JAzr3vdNh3ev0lOm8DOnGvpb?corpId=ding84ad0c7e075023e935c2f4657eb6378f&iframeQuery=utm_medium=im_card&utm_source=im&utm_medium=im_card&utm_scene=person_space&utm_source=im
 */
export default function GermanContent() {
  return (
    <>
      <WebOnly>
        <div className="w-full py-[50px] px-[200px] text-[14px] font-ir">
          <p className="text-center my-[25px] leading-[24px]">
            {RETURN_REFUND_INTRO_LINES.map((line, index) => (
              <span key={index} className="block">
                {renderContentWithContactLink(line)}
              </span>
            ))}
          </p>

          <div className="relative mb-[50px]">
            <Image
              src="static-page/return-refund/return-refund-01.jpeg"
              alt="Return & Refund"
              width={800}
              height={400}
              className="w-full"
            />
            <h1 className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center text-[36px] text-white font-bb">
              Rückgabe&Rückerstattung
            </h1>
          </div>

          <div className="space-y-[40px]">
            {RETURN_ISSUE_ITEMS.map((item) => (
              <p key={item.title} className="my-0 mx-auto leading-[24px] mb-[40px]">
                <strong className="font-bb">{item.title}</strong>{' '}
                {renderContentWithContactLink(item.content())}
              </p>
            ))}

            <h2 className="text-center font-bb my-[15px] text-[21px]">
              {RETURN_ORDER_CHANGE_SECTION.title}
            </h2>

            <p className="my-0 mx-auto leading-[24px] mb-[40px]">
              {RETURN_ORDER_CHANGE_SECTION.description}
            </p>

            <table className="my-[20px] mx-auto border-collapse border-spacing-0 w-[700px] border border-[#dddddd]">
              <tbody>
                <tr className="bg-[#eaedf6]">
                  <td className="bg-[#d6dae5] border border-[#dddddd] text-center h-[60px] align-middle break-words" />
                  <td className="border border-[#dddddd] text-center h-[60px] align-middle break-words">
                    {RETURN_ORDER_CHANGE_TABLE.headers.beforeShipping}
                  </td>
                  <td className="border border-[#dddddd] text-center h-[60px] align-middle break-words">
                    {RETURN_ORDER_CHANGE_TABLE.headers.afterShipping}
                  </td>
                </tr>
                <tr>
                  <td className="bg-[#eaedf6] border border-[#dddddd] text-center h-[60px] align-middle break-words">
                    {RETURN_ORDER_CHANGE_TABLE.rows[0].label}
                  </td>
                  <td className="border border-[#dddddd] text-center h-[60px] align-middle break-words">
                    {RETURN_ORDER_CHANGE_TABLE.rows[0].beforeShipping}
                  </td>
                  <td className="border border-[#dddddd] text-center h-[60px] align-middle break-words">
                    {RETURN_ORDER_CHANGE_TABLE.rows[0].afterShipping}
                  </td>
                </tr>
                <tr>
                  <td className="bg-[#eaedf6] border border-[#dddddd] text-center h-[60px] align-middle break-words">
                    {RETURN_ORDER_CHANGE_TABLE.rows[1].label}
                  </td>
                  <td className="border border-[#dddddd] text-center h-[60px] align-middle break-words">
                    {RETURN_ORDER_CHANGE_TABLE.rows[1].beforeShipping}
                  </td>
                  <td className="border border-[#dddddd] text-center h-[60px] align-middle break-words">
                    {RETURN_ORDER_CHANGE_TABLE.rows[1].afterShipping}
                  </td>
                </tr>
              </tbody>
            </table>

            <p className="my-0 mx-auto leading-[24px] mb-[40px]">
              <strong className="font-bb">Hinweis: </strong>Hier können Sie Ihren Bestellstatus
              verfolgen -
              <span className="text-[#0000ee]">
                <Link href="/track-order" className="text-[#0000ee]">
                  {' '}
                  Bestellung verfolgen
                </Link>
              </span>
            </p>

            <div className="flex">
              <div className="w-[276px] shrink-0 self-stretch overflow-hidden">
                <Image
                  src="/static-page/return-refund/return-refund-02.jpeg"
                  alt="How to return"
                  width={276}
                  height={200}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex min-w-0 flex-1 flex-col justify-center bg-[#f4f6f8] p-[20px_30px] box-border">
                <h2 className="text-center font-bb my-[15px] mt-0 text-[21px]">
                  {RETURN_GUIDE_SECTION.title}
                </h2>
                {RETURN_GUIDE_SECTION.items.map((item, index) => (
                  <p key={index} className="m-0 leading-[24px]">
                    {renderContentWithContactLink(item)}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </WebOnly>

      <WapOnly>
        <div className="px-[16px]">
          <div className="p-0">
            <p
              className="text-center my-[25px] leading-[24px] text-[12px]"
              style={{
                fontFamily: 'var(--font-bricolage)',
              }}
            >
              {RETURN_REFUND_INTRO_LINES.map((line, index) => (
                <span key={index} className="block">
                  {renderContentWithContactLink(line)}
                </span>
              ))}
            </p>

            <div className="space-y-[40px]">
              {RETURN_ISSUE_ITEMS.map((item) => (
                <p
                  key={item.title}
                  className="my-0 mx-auto mb-[40px] leading-[24px] text-[12px]"
                  style={{
                    fontFamily: 'var(--font-bricolage)',
                  }}
                >
                  <strong>{item.title}</strong> {renderContentWithContactLink(item.content())}
                </p>
              ))}
              <h2 className="text-center font-bb my-[15px] text-[18px]">
                {RETURN_ORDER_CHANGE_SECTION.title}
              </h2>
              <p
                className="my-0 text-[12px] mb-[40px] leading-[24px]"
                style={{
                  fontFamily: 'var(--font-bricolage)',
                }}
              >
                {renderContentWithContactLink(RETURN_ORDER_CHANGE_SECTION.description)}
              </p>
              <table className="my-[20px] mx-auto border-collapse border-spacing-0 w-full text-[12px] border border-[#dddddd]">
                <tbody>
                  <tr className="bg-[#eaedf6]">
                    <td className="bg-[#d6dae5] border border-[#dddddd] text-center h-[60px] align-middle break-words w-[33.33%] text-[12px]" />
                    <td className="border border-[#dddddd] text-center h-[60px] align-middle break-words w-[33.33%] text-[12px]">
                      {RETURN_ORDER_CHANGE_TABLE.headers.beforeShipping}
                    </td>
                    <td className="border border-[#dddddd] text-center h-[60px] align-middle break-words w-[33.33%] text-[12px]">
                      {RETURN_ORDER_CHANGE_TABLE.headers.afterShipping}
                    </td>
                  </tr>
                  <tr>
                    <td className="bg-[#eaedf6] border border-[#dddddd] text-center h-[60px] align-middle break-words w-[33.33%] text-[12px]">
                      {RETURN_ORDER_CHANGE_TABLE.rows[0].label}
                    </td>
                    <td className="border border-[#dddddd] text-center h-[60px] align-middle break-words w-[33.33%] text-[12px]">
                      {RETURN_ORDER_CHANGE_TABLE.rows[0].beforeShipping}
                    </td>
                    <td className="border border-[#dddddd] text-center h-[60px] align-middle break-words w-[33.33%] text-[12px]">
                      {RETURN_ORDER_CHANGE_TABLE.rows[0].afterShipping}
                    </td>
                  </tr>
                  <tr>
                    <td className="bg-[#eaedf6] border border-[#dddddd] text-center h-[60px] align-middle break-words w-[33.33%] text-[12px]">
                      {RETURN_ORDER_CHANGE_TABLE.rows[1].label}
                    </td>
                    <td className="border border-[#dddddd] text-center h-[60px] align-middle break-words w-[33.33%] text-[12px]">
                      {RETURN_ORDER_CHANGE_TABLE.rows[1].beforeShipping}
                    </td>
                    <td className="border border-[#dddddd] text-center h-[60px] align-middle break-words w-[33.33%] text-[12px]">
                      {RETURN_ORDER_CHANGE_TABLE.rows[1].afterShipping}
                    </td>
                  </tr>
                </tbody>
              </table>
              <p
                className="my-0 mx-auto mb-[40px] leading-[24px] text-[12px]"
                style={{ fontFamily: 'var(--font-bricolage)' }}
              >
                <strong> Hinweis:</strong> Hier können Sie Ihren Bestellstatus verfolgen -
                <Link href="/track-order" className="text-[#0000ee]">
                  {' '}
                  Bestellung verfolgen
                </Link>
              </p>
              <div className="flex flex-col w-full bg-[#f4f6f8] p-[20px_30px] box-border mb-[50px]">
                <h2 className="text-center font-bb my-[15px] mt-0">{RETURN_GUIDE_SECTION.title}</h2>
                {RETURN_GUIDE_SECTION.items.map((item, index) => (
                  <p
                    key={index}
                    className="m-0 leading-[24px] text-[12px]"
                    style={{ fontFamily: 'var(--font-bricolage)' }}
                  >
                    {renderContentWithContactLink(item)}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </WapOnly>
    </>
  );
}
